import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { UploadPickerModal } from './UploadPickerModal';
import { IssuePriority } from '../types/issues';
import { translateIssuePriority } from '../types/EnumTranslations';
import { Building, PhotoModel } from '../types/protocols';
import { BaseComponentImpl } from '../types/implementations';
import ComponentPickerModal from '../Pages/Förvaltning/components/ComponentPickerModal';
import { useCreateIssue } from '../hooks/issues/useCreateIssue';
import { createPhotoModelFromFile, uploadPhotoToDatabaseAndStorage } from '../Helpers/ImageUploadUtils';
import { getDatabase, ref, push } from 'firebase/database';
import { recordActivity } from '../api/activityApi';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import './ReportIssueModal.css';

interface ReportIssueModalProps {
    show: boolean;
    onHide: () => void;
    buildingKey: string;
    componentKey?: string;
    onSuccess?: () => void;
    /**
     * Felanmäl from a work order (#1650): the issue carries
     * `source: 'workOrder'` and this key.
     */
    sourceWorkOrderKey?: string;
    /**
     * The loaded building: shows a component picker (any component in the
     * building, or "Annat" for none) in place of the fixed `componentKey`.
     */
    building?: Building;
}

interface PhotoPreview {
    id: string;
    file: File;
    previewUrl: string;
}

const MAX_PHOTOS = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
    show,
    onHide,
    buildingKey,
    componentKey,
    onSuccess,
    sourceWorkOrderKey,
    building
}) => {
    const [user] = useAuthState(auth);
    const { submit, loading, error: submitError, clearError } = useCreateIssue();

    // Form state
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<IssuePriority>(IssuePriority.MEDIUM);
    const [photos, setPhotos] = useState<PhotoPreview[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedComponentKey, setSelectedComponentKey] = useState<string | undefined>(componentKey);
    const [showComponentPicker, setShowComponentPicker] = useState(false);
    const selectedComponentName = (() => {
        const raw = selectedComponentKey ? building?.model.structure?.components?.[selectedComponentKey] : undefined;
        return raw ? new BaseComponentImpl(raw).displayName() : undefined;
    })();

    // Reset form when modal opens/closes
    useEffect(() => {
        if (show) {
            setDescription('');
            setPriority(IssuePriority.MEDIUM);
            setPhotos([]);
            setError(null);
            setUploadProgress(null);
            setSelectedComponentKey(componentKey);
            clearError();
        }
    }, [show, clearError, componentKey]);

    // Cleanup preview URLs
    useEffect(() => {
        return () => {
            photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
        };
    }, [photos]);

    const validateFile = (file: File): string | null => {
        if (!file.type.startsWith('image/')) {
            return 'Endast bilder är tillåtna';
        }
        if (file.size > MAX_FILE_SIZE) {
            return `Filen ${file.name} är för stor (max 10 MB)`;
        }
        return null;
    };

    const addPhotos = (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const remainingSlots = MAX_PHOTOS - photos.length;

        if (fileArray.length > remainingSlots) {
            setError(`Max ${MAX_PHOTOS} bilder tillåtna. Du kan lägga till ${remainingSlots} till.`);
            return;
        }

        const newPhotos: PhotoPreview[] = [];
        for (const file of fileArray) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                continue;
            }

            newPhotos.push({
                id: `${Date.now()}-${Math.random()}`,
                file,
                previewUrl: URL.createObjectURL(file)
            });
        }

        setPhotos(prev => [...prev, ...newPhotos]);
        setError(null);
    };

    const removePhoto = (id: string) => {
        setPhotos(prev => {
            const photo = prev.find(p => p.id === id);
            if (photo) {
                URL.revokeObjectURL(photo.previewUrl);
            }
            return prev.filter(p => p.id !== id);
        });
    };

    const handleFiles = (files: File[]) => {
        const imageFiles = files.filter(f => f.type.startsWith('image/'));
        if (imageFiles.length > 0) {
            addPhotos(imageFiles);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        if (files.length > 0) {
            addPhotos(files);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!description.trim()) {
            setError('Beskrivning krävs');
            return;
        }

        if (description.length > 2000) {
            setError('Beskrivning får inte vara längre än 2000 tecken');
            return;
        }

        if (!user) {
            setError('Du måste vara inloggad');
            return;
        }

        setIsUploading(true);

        try {
            // Prepare photo data with explicit key-to-file relationships
            const photoUploads: Array<{
                file: File;
                key: string;
                model: PhotoModel;
            }> = [];

            if (photos.length > 0) {
                const db = getDatabase();

                for (const photo of photos) {
                    const photosRef = ref(db, 'issues/temp');
                    const newPhotoRef = push(photosRef);
                    const photoKey = newPhotoRef.key!;
                    const storagePath = `issues/temp/photos/${photoKey}`;

                    const photoModel = await createPhotoModelFromFile(
                        photoKey,
                        storagePath,
                        user.uid,
                        photo.file
                    );

                    photoUploads.push({
                        file: photo.file,
                        key: photoKey,
                        model: photoModel
                    });
                }
            }

            // Build photos object for issue creation
            const uploadedPhotos: { [key: string]: PhotoModel } | undefined =
                photoUploads.length > 0
                    ? Object.fromEntries(photoUploads.map(p => [p.key, p.model]))
                    : undefined;

            // Create the issue
            const issueKey = await submit({
                buildingKey,
                componentKey: building ? selectedComponentKey : componentKey,
                ...(sourceWorkOrderKey ? { source: 'workOrder' as const, sourceWorkOrderKey } : {}),
                description: description.trim(),
                priority,
                photos: uploadedPhotos
            });
            recordActivity('issue.created', { buildingKey, targetKey: issueKey });

            // Upload photos to storage with correct paths
            if (photoUploads.length > 0) {
                setUploadProgress({ current: 0, total: photoUploads.length });
            }

            for (let i = 0; i < photoUploads.length; i++) {
                const { file, key, model } = photoUploads[i];
                const correctStoragePath = `issues/${issueKey}/photos/${key}`;
                const correctDbPath = `issues/active/${issueKey}/photos/${key}`;

                const updatedPhotoModel: PhotoModel = {
                    ...model,
                    storagePath: correctStoragePath
                };

                await uploadPhotoToDatabaseAndStorage(
                    correctDbPath,
                    correctStoragePath,
                    updatedPhotoModel,
                    file
                );

                setUploadProgress({ current: i + 1, total: photoUploads.length });
            }

            // Success - close modal and notify parent
            onHide();
            if (onSuccess) {
                onSuccess();
            }
        } catch (err: any) {
            console.error('Error creating issue:', err);
            setError(err.message || 'Kunde inte skapa felanmälan');
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };

    const isSubmitting = loading || isUploading;

    return (
        <>
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{sourceWorkOrderKey ? 'Felanmäl nytt fel' : 'Rapportera fel'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {(error || submitError) && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => { setError(null); clearError(); }}
                        >
                            {error || submitError}
                        </Alert>
                    )}

                    {/* Description */}
                    <Form.Group className="mb-3">
                        <Form.Label>Beskrivning *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Beskriv problemet..."
                            maxLength={2000}
                            required
                            disabled={isSubmitting}
                        />
                        <Form.Text className="text-muted">
                            {description.length}/2000 tecken
                        </Form.Text>
                    </Form.Group>

                    {/* Component: any in the building, or "Annat" */}
                    {building && (
                        <Form.Group className="mb-3">
                            <Form.Label>Komponent</Form.Label>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className={selectedComponentName ? undefined : 'text-muted'}>
                                    {selectedComponentName || 'Annat (ingen komponent)'}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setShowComponentPicker(true)}
                                    disabled={isSubmitting}
                                >
                                    {selectedComponentKey ? 'Byt' : 'Välj komponent'}
                                </Button>
                                {selectedComponentKey && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() => setSelectedComponentKey(undefined)}
                                        disabled={isSubmitting}
                                    >
                                        Annat
                                    </Button>
                                )}
                            </div>
                        </Form.Group>
                    )}

                    {/* Priority */}
                    <Form.Group className="mb-3">
                        <Form.Label>Prioritet</Form.Label>
                        <Form.Select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value as IssuePriority)}
                            disabled={isSubmitting}
                        >
                            {Object.values(IssuePriority).map(p => (
                                <option key={p} value={p}>
                                    {translateIssuePriority(p)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Photo Upload */}
                    <Form.Group className="mb-3">
                        <Form.Label>Bilder (valfritt)</Form.Label>
                        <div
                            className={`report-issue-dropzone${isDragOver ? ' report-issue-dropzone--drag-over' : ''}${isSubmitting ? ' report-issue-dropzone--disabled' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => !isSubmitting && setShowUploadModal(true)}
                        >
                            <div className="report-issue-dropzone__text">
                                {isDragOver ? (
                                    <span>Släpp bilder här</span>
                                ) : (
                                    <span>
                                        Dra och släpp bilder här, eller klicka för att välja
                                        <br />
                                        <small>Max {MAX_PHOTOS} bilder, 10 MB per bild</small>
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Photo Previews */}
                        {photos.length > 0 && (
                            <div className="report-issue-photos">
                                {photos.map(photo => (
                                    <div key={photo.id} className="report-issue-photo">
                                        <img
                                            src={photo.previewUrl}
                                            alt="Preview"
                                            className="report-issue-photo__image"
                                        />
                                        <button
                                            type="button"
                                            className="report-issue-photo__remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removePhoto(photo.id);
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                {uploadProgress
                                    ? `Laddar upp bild ${uploadProgress.current}/${uploadProgress.total}...`
                                    : 'Skickar...'
                                }
                            </>
                        ) : (
                            'Skicka'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>

        {building && (
            <ComponentPickerModal
                show={showComponentPicker}
                onHide={() => setShowComponentPicker(false)}
                building={building}
                currentComponentKey={selectedComponentKey}
                onSelect={(key) => {
                    setSelectedComponentKey(key ?? undefined);
                    setShowComponentPicker(false);
                }}
            />
        )}

        <UploadPickerModal
            show={showUploadModal}
            onHide={() => setShowUploadModal(false)}
            onFilesSelected={handleFiles}
            accept="image/*"
            title="Lägg till bilder"
            multiple
        />
        </>
    );
};

export default ReportIssueModal;
